
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CreatorPaymentSettingsProps {
  userId: string;
}

export const CreatorPaymentSettings = ({ userId }: CreatorPaymentSettingsProps) => {
  const queryClient = useQueryClient();
  const [subscriptionPrice, setSubscriptionPrice] = useState("");
  const [lifetimePrice, setLifetimePrice] = useState("");

  const { data: profile, isLoading: profileLoading, error } = useQuery({
    queryKey: ["creator-profile", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("subscription_price, lifetime_subscription_price")
        .eq("id", userId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (profile) {
      setSubscriptionPrice(profile.subscription_price?.toString() || "");
      setLifetimePrice(profile.lifetime_subscription_price?.toString() || "");
    }
  }, [profile]);

  const updatePricesMutation = useMutation({
    mutationFn: async ({
      subscriptionPrice,
      lifetimePrice,
    }: {
      subscriptionPrice: number;
      lifetimePrice: number;
    }) => {
      const { error } = await supabase
        .from("profiles")
        .update({
          subscription_price: subscriptionPrice,
          lifetime_subscription_price: lifetimePrice,
        })
        .eq("id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-profile"] });
      toast.success("Prices updated successfully");
    },
    onError: (error) => {
      console.error("Price update error:", error);
      toast.error("Failed to update prices - please try again");
    },
  });

  const handleUpdatePrices = () => {
    const numericSubscriptionPrice = parseFloat(subscriptionPrice);
    const numericLifetimePrice = parseFloat(lifetimePrice);

    // Additional validation
    if (subscriptionPrice === "" || lifetimePrice === "") {
      toast.error("Please enter both prices");
      return;
    }

    if (isNaN(numericSubscriptionPrice) || isNaN(numericLifetimePrice)) {
      toast.error("Please enter valid prices");
      return;
    }

    if (numericSubscriptionPrice < 0.99) {
      toast.error("Minimum monthly subscription price is $0.99");
      return;
    }

    if (numericLifetimePrice < 9.99) {
      toast.error("Minimum lifetime price is $9.99");
      return;
    }

    if (numericLifetimePrice <= numericSubscriptionPrice) {
      toast.error("Lifetime price must be higher than monthly subscription price");
      return;
    }

    updatePricesMutation.mutate({
      subscriptionPrice: numericSubscriptionPrice,
      lifetimePrice: numericLifetimePrice,
    });
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load payment settings. Please refresh the page to try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (profileLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Payment Settings</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Configure your subscription prices. The platform takes a 15% commission from all payments.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium">Monthly Subscription Price ($)</label>
          <Input
            type="number"
            min="0.99"
            step="0.01"
            value={subscriptionPrice}
            onChange={(e) => setSubscriptionPrice(e.target.value)}
            placeholder="Enter monthly subscription price"
            className="max-w-xs mt-1"
          />
          <p className="text-sm text-muted-foreground mt-1">
            You'll receive ${((parseFloat(subscriptionPrice) || 0) * 0.85).toFixed(2)} after platform fee
          </p>
        </div>

        <div>
          <label className="text-sm font-medium">Lifetime Access Price ($)</label>
          <Input
            type="number"
            min="9.99"
            step="0.01"
            value={lifetimePrice}
            onChange={(e) => setLifetimePrice(e.target.value)}
            placeholder="Enter lifetime access price"
            className="max-w-xs mt-1"
          />
          <p className="text-sm text-muted-foreground mt-1">
            You'll receive ${((parseFloat(lifetimePrice) || 0) * 0.85).toFixed(2)} after platform fee
          </p>
        </div>

        <Button 
          onClick={handleUpdatePrices}
          disabled={updatePricesMutation.isPending}
        >
          {updatePricesMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating...
            </>
          ) : (
            'Update Prices'
          )}
        </Button>
      </div>
    </Card>
  );
};
